import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  Outlet,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { Toaster } from "react-hot-toast";

import { InsufficientBalanceModal } from "@/components/billing/InsufficientBalanceModal";
import { PaymentRequiredModal } from "@/components/billing/PaymentRequiredModal";
import { StorageLimitExceededModal } from "@/components/billing/StorageLimitExceededModal";
import { ConfirmProvider } from "@/components/layout/confirm";
import { VideoGenerationBar } from "@/components/video/VideoGenerationBar";
import { VideoReadyModal } from "@/components/video/VideoReadyModal";
import { queryClient } from "@/queryClient";
import { useThemeStore } from "@/stores/theme.store";

export interface RouterContext {
  queryClient: typeof queryClient;
}

function RootErrorComponent({ error, reset }: ErrorComponentProps) {
  Sentry.captureException(error);
  return (
    <div className="flex min-h-screen items-center justify-center bg-base px-4">
      <div className="w-full max-w-md text-center">
        <h1 className="text-2xl font-bold text-primary">
          Cos poszlo nie tak
        </h1>
        <p className="mt-2 text-sm text-tertiary">
          {error.message || "Wystapil nieoczekiwany blad."}
        </p>
        <button
          type="button"
          onClick={reset}
          className="mt-6 inline-flex items-center rounded-xl bg-accent shadow-glow px-4 py-2 text-sm font-medium text-white hover:shadow-glow-strong"
        >
          Sprobuj ponownie
        </button>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  errorComponent: RootErrorComponent,
});

function RootComponent() {
  useThemeStore();

  return (
    <QueryClientProvider client={queryClient}>
      <ConfirmProvider>
        <Outlet />
        <Toaster
          containerClassName={"mt-16"}
          position={"top-right"}
          toastOptions={{
            duration: 2000,
            style: {
              background: "var(--bg-surface)",
              color: "var(--text-primary)",
              border: "1px solid var(--border)",
            },
          }}
        />
        <InsufficientBalanceModal />
        <PaymentRequiredModal />
        <StorageLimitExceededModal />
        <VideoGenerationBar />
        <VideoReadyModal />
      </ConfirmProvider>
    </QueryClientProvider>
  );
}
